import { RiTimeLine } from "@remixicon/react";
import React, { ReactElement } from "react";
import { hoursToTime, timeToHoursWithValidation } from "../../../date";

import Select from "../../select/AsyncSelect";
import Styles from "./TimePicker.module.scss";

export class Time {
    private text: string;

    public static fromUtcDate(date: Date): Time {
        return new Time(date.getUTCHours() * 60 + date.getUTCMinutes());
    }

    public static fromDate(date: Date): Time {
        return new Time(date.getHours() * 60 + date.getMinutes());
    }

    constructor(public readonly time: number) {
        this.text = new Date(0, 0, 0, this.time / 60, this.time % 60).toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "numeric",
        });
    }

    get hours(): number {
        return Math.floor(this.time / 60);
    }

    get minutes(): number {
        return this.time % 60;
    }

    toUTCString(): string {
        return hoursToTime(this.time);
    }

    toString() {
        return this.text;
    }
}

function loadTimes() {
    const times = [];
    for (let i = 0; i < 24; i++) {
        for (let j = 0; j < 60; j += 15) {
            times.push(new Time((i * 60) + j));
        }
    }
    return times;
}
const DEFAULT_TIMES = loadTimes();

interface Props<TRequired extends boolean> {
    onChange: (value: TRequired extends true ? Time : (Time | undefined)) => void;
    isRequired?: TRequired;
    value?: TRequired extends true ? (Time | number) : (Time | number | undefined);
    placeholder?: string;
    className?: string;
}

const HOUR_12 = 12 * 60;

export default function TimePicker<TRequired extends boolean>({
    onChange: onSelected, isRequired, value, placeholder, className,
}: Props<TRequired>): ReactElement {
    let time;
    if (typeof value === "number") {
        time = new Time(value);
    } else if (value instanceof Date) {
        time = new Time(value.getUTCHours() * 60 + value.getUTCMinutes());
    } else {
        time = value as Time;
    }
    return (
        <Select
            className={className}
            minRequired={isRequired ? 1 : 0}
            isClearable={!isRequired}
            placeholder={placeholder}
            dropdownIcon={<RiTimeLine className={Styles["time-picker-dropdown"]} />}
            loadOptions={async (inputValue: string) => {
                const results = DEFAULT_TIMES.filter(current => current.toString().includes(inputValue));
                if (results.length === 0) {
                    const search = inputValue.toLowerCase();
                    let parsed;
                    if (search.includes(":")) {
                        parsed = timeToHoursWithValidation(search);
                    } else {
                        const numeric = parseInt(search, 10);
                        if (numeric < 24) {
                            parsed = numeric * 60;
                        } else if (numeric < 100) {
                            parsed = (Math.floor(numeric / 10) * 60) + (numeric % 10);
                        } else if (numeric < 2400) {
                            parsed = (Math.floor(numeric / 100) * 60) + (numeric % 100);
                        }
                    }
                    const isAm = search.includes("a");
                    const isPm = search.includes("p");
                    if (parsed) {
                        const hours = Math.floor(parsed / 60);
                        if (isAm && hours === 12) {
                            parsed -= HOUR_12;
                        } else if (isPm && hours < 12) {
                            parsed += HOUR_12;
                        }

                        results.push(new Time(parsed));
                        if (parsed < HOUR_12 && !isAm) {
                            results.push(new Time(parsed + HOUR_12));
                        }
                    }
                }
                return results.map(item => ({
                    label: item.toString(),
                    value: item.time.toString(),
                    data: item,
                }));
            }}
            onChange={option => onSelected(option?.data as Time)}
            // options={DEFAULT_TIMES.map(item => ({
            //     label: item.toString(),
            //     value: item.time.toString(),
            //     data: item,
            // }))}
            value={time ? {
                label: time.toString(),
                value: time.time.toString(),
            } : undefined}
        />
    );
}
TimePicker.defaultProps = {
    isRequired: false,
    value: undefined,
    placeholder: undefined,
    className: "",
};
